import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import styles from './index.module.scss';
import classNames from 'classnames';
import { GasMonitorContext } from './context';
import { ShareDataWrapperContext } from '../..';
import { useRequest } from 'ahooks';
import dayjs, { Dayjs } from 'dayjs';
import {
  apiV2GasMonitorConfigDetailPost,
  apiV2GasMonitorMapGasCostTrendPost,
  apiV2GasMonitorMapGasUsageTrendPost,
  apiV2GasMonitorMapGetEnergyCostPost,
  apiV2GasMonitorMapGetEnergyUsagePost,
  apiV2GasMonitorMapGetMonthlyAndAnnualGasUsagePost,
} from '@maxtropy/device-customer-apis-v2';
import LeftBox from './components/LeftBox';
import RightBox from './components/RightBox';
import TopMetrics from './components/TopMetrics';
import CenterBottomBox from './components/CenterBottomBox';
import { UnityOverlay, useSharedUnityMap } from '../SharedUnityMapLayer';
import MapView from './components/MapView';
import { IndexTimeActive } from './components/LeftBox/SwitchDate';
import { getDefaultTime } from '../../utils';
import type { WgqMapMode } from '../..';
import type { UnityClickDetail } from '../UnityRenderMap';
import AreaGasOverviewModal from './components/Modals/AreaGasOverviewModal';
import StationGasOverviewModal from './components/Modals/StationGasOverviewModal';

const GAS_STATION_NAMES = new Set(['1#气体站房', '2#气体站房', '3#气体站房']);
const GAS_PIPELINE_PATHS = ['ng', 'steampipe', 'oxygen'];
const XRAY_MODE_TIMEOUT = 2000;

const getUnityBooleanResult = (value: unknown, fallback: boolean) => {
  if (typeof value === 'boolean') return value;
  if (value === 'true') return true;
  if (value === 'false') return false;
  if (value && typeof value === 'object') {
    const data = value as Record<string, unknown>;
    if (typeof data.enabled === 'boolean') return data.enabled;
    if (typeof data.data === 'boolean') return data.data;
    if (typeof data.value === 'boolean') return data.value;
  }
  return fallback;
};

const withUnityTimeout = async <T,>(promise: Promise<T>, timeout: number, message: string): Promise<T> => {
  let timer: number | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timer = window.setTimeout(() => {
          reject(new Error(message));
        }, timeout);
      }),
    ]);
  } finally {
    if (timer) {
      window.clearTimeout(timer);
    }
  }
};

const UnityGasMonitorMap: React.FC<{ mapMode?: WgqMapMode }> = ({ mapMode = 'unityMap' }) => {
  const { isStandardSize, loadMap } = useContext(ShareDataWrapperContext);
  const { unityReady, callUnity, registerUnityClickHandler } = useSharedUnityMap();
  const [activeKey, setActiveKey] = useState<IndexTimeActive>(IndexTimeActive.THIS_MONTH);
  const [value, setValue] = useState<Dayjs>();
  const [areaOverviewOpen, setAreaOverviewOpen] = useState(false);
  const [stationOverviewOpen, setStationOverviewOpen] = useState(false);
  const [selectedAreaName, setSelectedAreaName] = useState('');
  const [selectedZoneName, setSelectedZoneName] = useState('');
  const [isXrayMode, setIsXrayMode] = useState(false);
  const isXrayModeRef = useRef(false);

  useEffect(() => {
    // 如果当前是1号，则默认时间为上个月
    const date = dayjs().date() === 1 ? dayjs().subtract(1, 'day') : dayjs();
    setValue(date);
  }, []);

  useEffect(() => {
    isXrayModeRef.current = isXrayMode;
  }, [isXrayMode]);

  const setPipelineVisible = useCallback(
    async (active: boolean) => {
      if (!callUnity) {
        return;
      }

      await Promise.all(
        GAS_PIPELINE_PATHS.map(async path => {
          const payload = {
            type: 'setObjectActive',
            data: {
              path,
              active,
            },
          };

          try {
            return await withUnityTimeout(
              callUnity(payload),
              XRAY_MODE_TIMEOUT,
              `[UnityGasMonitorMap] setObjectActive timeout: ${path}`
            );
          } catch (error) {
            console.error('[UnityGasMonitorMap] setObjectActive failed', { path, active, error });
            return undefined;
          }
        })
      );
    },
    [callUnity]
  );

  const closeXrayMode = useCallback(async () => {
    if (!callUnity) {
      return;
    }

    await setPipelineVisible(false);

    const payload = {
      type: 'setXrayMode',
      data: {
        enabled: false,
      },
    };
    await withUnityTimeout(
      callUnity(payload),
      XRAY_MODE_TIMEOUT,
      '[UnityGasMonitorMap] close setXrayMode timeout'
    ).catch(error => {
      console.error('[UnityGasMonitorMap] close xray mode failed', error);
    });
  }, [callUnity, setPipelineVisible]);

  useEffect(() => {
    if (loadMap && mapMode === 'unityMap') return;
    if (isXrayModeRef.current) {
      void closeXrayMode();
    }
    setIsXrayMode(false);
  }, [closeXrayMode, loadMap, mapMode]);

  useEffect(() => {
    return () => {
      if (!isXrayModeRef.current) return;
      void closeXrayMode();
    };
  }, [closeXrayMode]);

  const handleClickXray = useCallback(async () => {
    if (!unityReady || !callUnity) {
      return;
    }

    const enable = !isXrayMode;

    try {
      const payload = {
        type: 'setXrayMode',
        data: {
          enabled: enable,
        },
      };
      const result = await withUnityTimeout(
        callUnity(payload),
        XRAY_MODE_TIMEOUT,
        '[UnityGasMonitorMap] setXrayMode timeout'
      ).catch(() => undefined);
      const nextXrayMode = getUnityBooleanResult(result, enable);

      await setPipelineVisible(nextXrayMode);
      setIsXrayMode(nextXrayMode);
    } catch (error) {
      console.error('[UnityGasMonitorMap] switch xray mode failed', error);
      if (enable) {
        void closeXrayMode();
      }
    }
  }, [callUnity, closeXrayMode, isXrayMode, setPipelineVisible, unityReady]);

  const renderXrayButton = useCallback(() => {
    if (!loadMap || mapMode !== 'unityMap') return null;

    return (
      <div className={classNames(styles.xrayLegendList, isStandardSize ? styles.xrayLegendListStandard : '')}>
        <button
          className={classNames(styles.xrayButton, isXrayMode ? styles.xrayButtonActive : '')}
          type="button"
          onClick={handleClickXray}
          disabled={!unityReady || !callUnity}
          title="切换透视模式"
        >
          透视模式
        </button>
      </div>
    );
  }, [callUnity, handleClickXray, isStandardSize, isXrayMode, loadMap, mapMode, unityReady]);

  // 查询配置
  const { data: configDetail } = useRequest(async () => {
    return await apiV2GasMonitorConfigDetailPost({});
  });

  // 气体成本
  const { data: energyCostData, loading: energyCostLoading } = useRequest(
    () =>
      apiV2GasMonitorMapGetEnergyCostPost({
        timeResolution: activeKey,
        fromTime: getDefaultTime(activeKey!, value)?.startTime,
        toTime: getDefaultTime(activeKey!, value)?.endTime,
      }),
    {
      ready: !!value && !!activeKey,
      refreshDeps: [value],
    }
  );

  // 用量成本统计
  const { data: energyUsageData, loading: energyUsageLoading } = useRequest(
    () =>
      apiV2GasMonitorMapGetEnergyUsagePost({
        timeResolution: activeKey,
        fromTime: getDefaultTime(activeKey!, value)?.startTime,
        toTime: getDefaultTime(activeKey!, value)?.endTime,
      }),
    {
      ready: !!value && !!activeKey,
      refreshDeps: [value],
    }
  );

  const { data: monthAndAnnualData, loading: monthAndAnnualLoading } = useRequest(
    apiV2GasMonitorMapGetMonthlyAndAnnualGasUsagePost,
    {
      ready: !!value && !!activeKey,
      refreshDeps: [value],
    }
  );
  // 用气趋势
  const { data: gasUsageTrendData, loading: gasUsageTrendLoading } = useRequest(
    () =>
      apiV2GasMonitorMapGasUsageTrendPost({
        timeResolution: activeKey,
        fromTime: getDefaultTime(activeKey!, value)?.startTime,
        toTime: getDefaultTime(activeKey!, value)?.endTime,
      }),
    {
      ready: !!value && !!activeKey,
      refreshDeps: [value],
    }
  );

  // 成本趋势
  const { data: gasCostTrendData, loading: gasCostTrendLoading } = useRequest(
    () =>
      apiV2GasMonitorMapGasCostTrendPost({
        timeResolution: activeKey,
        fromTime: getDefaultTime(activeKey!, value)?.startTime,
        toTime: getDefaultTime(activeKey!, value)?.endTime,
      }),
    {
      ready: !!value && !!activeKey,
      refreshDeps: [value],
    }
  );

  const handleUnityClick = useCallback((detail: UnityClickDetail) => {
    const clickData = detail?.data ?? {};
    const areaName = String(clickData.areaName ?? clickData.name ?? clickData.zoneName ?? '').trim();
    const zoneName = String(clickData.zoneName ?? clickData.areaName ?? clickData.name ?? '').trim();
    if (!areaName && !zoneName) return;

    setSelectedAreaName(areaName || zoneName);
    setSelectedZoneName(zoneName || areaName);
    if (GAS_STATION_NAMES.has(String(clickData.name ?? '').trim())) {
      setAreaOverviewOpen(false);
      setStationOverviewOpen(true);
      return;
    }

    setStationOverviewOpen(false);
    setAreaOverviewOpen(true);
  }, []);

  useEffect(() => registerUnityClickHandler(handleUnityClick), [handleUnityClick, registerUnityClickHandler]);

  // 标准分辨率渲染
  const renderStandardScreen = useMemo(() => {
    return (
      <div className={styles.standard_no_map_screen}>
        <div className={styles.standard_no_map_scroll}>
          {!loadMap && <TopMetrics className={styles.standard_no_map_top_metrics} />}
          <div
            className={classNames(
              styles.content_layer,
              !loadMap ? styles.content_layer_no_map : '',
              !loadMap ? styles.standard_no_map_content : ''
            )}
            style={{ justifyContent: !loadMap ? 'center' : undefined, gap: !loadMap ? 8 : undefined }}
          >
            <div className={styles.center_left_box} style={{ flex: !loadMap ? 1 : undefined }}>
              <LeftBox />
            </div>
            <div className={styles.center_right_box} style={{ flex: !loadMap ? 1 : undefined }}>
              <RightBox />
            </div>
          </div>
        </div>
        {!loadMap && <CenterBottomBox />}
      </div>
    );
  }, [loadMap]);

  const renderStandardMapScreen = useMemo(() => {
    return (
      <div className={styles.inner_center}>
        {mapMode === 'unityMap' && <UnityOverlay className={styles.map}></UnityOverlay>}
        {mapMode === 'mapView' && <MapView className={styles.map}></MapView>}
        {renderXrayButton()}
      </div>
    );
  }, [mapMode, renderXrayButton]);

  // 非标准分辨率渲染（大屏模式）
  const renderLargeScreen = useMemo(() => {
    return (
      <div
        className={classNames(styles.inner_center, !loadMap ? styles.inner_center_no_map : '')}
        style={{ background: !loadMap ? '#111D2C' : 'transparent' }}
      >
        {mapMode === 'unityMap' && loadMap && <UnityOverlay className={styles.map}></UnityOverlay>}
        {mapMode === 'mapView' && loadMap && <MapView className={styles.map}></MapView>}
        {renderXrayButton()}

        {loadMap ? (
          <div className={styles.center_center_box}>
            <TopMetrics className={styles.center_top_box} />
            <CenterBottomBox className={styles.center_bottom_box} />
          </div>
        ) : (
          <CenterBottomBox />
        )}

        <div
          className={classNames(styles.content_layer, !loadMap ? styles.content_layer_no_map : '')}
          style={{ justifyContent: !loadMap ? 'center' : undefined, gap: !loadMap ? 8 : undefined }}
        >
          <div
            className={classNames(
              styles.center_left_box,
              !loadMap ? styles.noMapWidth : '',
              !loadMap ? styles.noMapColumn : ''
            )}
          >
            {!loadMap && <TopMetrics className={styles.no_map_top_metrics} />}
            <LeftBox />
          </div>
          <div
            className={classNames(
              styles.center_right_box,
              !loadMap ? styles.noMapWidth : '',
              !loadMap ? styles.noMapColumn : ''
            )}
          >
            <RightBox />
          </div>
        </div>
      </div>
    );
  }, [loadMap, mapMode, renderXrayButton]);

  return (
    <GasMonitorContext.Provider
      value={{
        timeResolution: activeKey,
        setActiveKey,
        dateValue: value,
        setValue,
        configDetail,
        configLoading: false,
        energyCostData,
        energyCostLoading,
        energyUsageData,
        energyUsageLoading,
        monthAndAnnualData,
        monthAndAnnualLoading,
        gasUsageTrendData,
        gasUsageTrendLoading,
        gasCostTrendData,
        gasCostTrendLoading,
      }}
    >
      {isStandardSize ? (loadMap ? renderStandardMapScreen : renderStandardScreen) : renderLargeScreen}
      <AreaGasOverviewModal
        open={areaOverviewOpen}
        areaName={selectedAreaName}
        zoneName={selectedZoneName}
        onClose={() => setAreaOverviewOpen(false)}
      />
      <StationGasOverviewModal
        open={stationOverviewOpen}
        stationName={selectedAreaName}
        zoneName={selectedZoneName}
        onClose={() => setStationOverviewOpen(false)}
      />
    </GasMonitorContext.Provider>
  );
};

export default UnityGasMonitorMap;
